// The stages of the open pipeline in scope: how many opportunities and their pipeline value, with the
// flags beneath. Each box filters the table to its stage. New Business and Expansion always show stages 1-5;
// renewals show only the stages their rows are in (buildFunnel).
//
// No stage is special on this board (plan.md): Qualification is shown like the rest, so a reader
// sees where the flags sit against where the money sits.

import { formatMoney } from '../board/format.ts';
import type { Funnel, FunnelStage } from '../board/view.ts';

// Always three lines in the same order, so every box is the same height whatever the counts.
function Flag({ count, label }: { count: number; label: string }) {
  return (
    <span className={'stage-flag' + (count > 0 ? ' is-bad' : '')}>
      <b className="num">{count}</b> {label}
    </span>
  );
}

// The count and the amount are equally important here: one size and weight, told apart by colour.
function StageBox({
  stage, selected, onSelect,
}: {
  stage: FunnelStage;
  selected: boolean;
  onSelect: (stageNumber: number) => void;
}) {
  const stageNumber = stage.stage_number ?? 0;
  return (
    <button
      type="button"
      className={'stage' + (stage.count === 0 ? ' stage-empty' : '')}
      aria-pressed={selected}
      onClick={() => onSelect(stageNumber)}
      title={`Show only ${stage.label}`}
    >
      <span className="stage-name">{stage.label}</span>
      <span className="stage-figure stage-count num">
        {stage.count} <span className="stage-unit">{stage.count === 1 ? 'opportunity' : 'opportunities'}</span>
      </span>
      <span className="stage-figure stage-amount num">{formatMoney(stage.pipeline_value)}</span>
      <span className="stage-flags">
        <Flag count={stage.stale_count} label="stale" />
        <Flag count={stage.no_rep_next_step_count} label="no next step" />
        <Flag count={stage.past_close_count} label="past close" />
      </span>
    </button>
  );
}

export function StageFunnel({
  funnel, selectedStage, onSelect,
}: {
  funnel: Funnel;
  selectedStage: number | null;
  onSelect: (stageNumber: number) => void;
}) {
  return (
    <section className="funnel" aria-label="By stage">
      <h2 className="section-title">By stage</h2>
      <div className="funnel-stages">
        {funnel.stages.map(stage => (
          <StageBox
            key={stage.stage_number}
            stage={stage}
            selected={selectedStage === stage.stage_number}
            onSelect={onSelect}
          />
        ))}
      </div>
      {funnel.others.length > 0 ? (
        <ul className="funnel-others">
          {funnel.others.map(stage => (
            <li key={stage.label}>
              <span>{stage.label}</span>
              <span className="num">{stage.count} · {formatMoney(stage.pipeline_value)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
